import { IMAGE_PATHS } from './paths.constants';
import Acquisition07 from '../components/micro/learningCheck/childNodes/Acquisition07';

export interface ImageFormat {
  path: string;
  alt: string;
}

export interface QuesionFormat {
  option?: string;
  questionText: string;
  questionImages?: ImageFormat[];
  questionCaptions?: string[];
}

export interface CorrectFormat {
  option?: string;
  correctText: string;
  images?: ImageFormat[];
  captions?: string[];
  customText?: string;
}

export interface ComprehensiveFormat {
  comprehensiveText: string;
  comprehensiveImages?: ImageFormat[];
  comprehensiveCaptions?: string[];
}

export interface ChildNodeProps {
  setSelectedAnswer: (canCheck: boolean) => void;
}

export interface LearningCheckFormat {
  number: number;
  question: QuesionFormat;
  options: string[];
  answerIds?: number[];
  optionsUnlabeled?: boolean;
  correct: CorrectFormat;
  comprehensiveQuestion?: string;
  comprehensiveCorrect?: ComprehensiveFormat;
  childNode?: ({ setSelectedAnswer }: ChildNodeProps) => JSX.Element;
}

export interface LearningCheckDictionary {
  [module: string]: {
    [page: string]: LearningCheckFormat[];
  };
}

export const OPTIONS = ['A', 'B', 'C', 'D', 'E', 'F'];
const LEARNING_CHECK: LearningCheckDictionary = {
  axuminInterpretation: {
    '3': [
      {
        number: 1,
        question: {
          questionText:
            'Amino acid transporters are upregulated into many cancer cells. What are the principal transporters involved in Axumin uptake for more aggressive phenotypes of prostate cancer?',
        },
        options: ['LAT2 and ASCT1', 'SLC7A8 and ASCT1', 'LAT1 and ASCT2', 'LAT3 and ASCT1'],
        answerIds: [1, 2, 3, 4],
        correct: {
          option: 'C',
          correctText:
            'LAT1 and ASCT2 expression levels have been correlated with a more aggressive phenotype of prostate cancer.',
        },
      },
      {
        number: 2,
        question: {
          questionText: 'Does uptake in muscle increase or decrease over time post Axumin injection?',
        },
        options: ['Increase', 'Decrease'],
        answerIds: [5, 6],
        correct: {
          option: 'A',
          correctText: 'Over time, Axumin distribution to the skeletal muscle <b>increases</b>.',
        },
      },
      {
        number: 3,
        question: {
          questionText:
            'To take advantage of the highest tumor-to-normal tissue contrast, what is the critical time frame within which to begin PET imaging in the pelvis?',
        },
        options: [
          '10 to 20 minutes after injection',
          '1 to 3 minutes after injection',
          '3 to 5 minutes after injection',
          'Up to 90 minutes after injection',
        ],
        answerIds: [7, 8, 9, 10],
        correct: {
          option: 'C',
          correctText:
            'PET scanning should begin 3 to 5 minutes after injection. The tumor-to-normal tissue contrast is highest between 4 and 10 minutes post injection.',
        },
      },
    ],
    '5': [
      {
        number: 4,
        question: {
          questionText:
            'Axumin injection into the _______ arm is preferred to avoid image interpretation error due to ______?',
        },
        options: [
          'Right AND stasis in left axillary vein',
          'Right AND urinary stasis',
          'Left AND misregistration',
          'Left AND hypermetabolic lesions',
        ],
        answerIds: [11, 12, 13, 14],
        correct: {
          option: 'A',
          correctText:
            'Injection into right arm is preferred to avoid image interpretation error. Beware of the possibility of image interpretation as stasis in left axillary vein may be misinterpreted as metastatic lymph nodes (Virchow’s node).',
          images: [IMAGE_PATHS.STATIS_VEIN, IMAGE_PATHS.VIRCHOWS_NODE],
          captions: ['Stasis (or venous trapping in axillary vein (SUV<sub>max</sub> = 201)', 'Virchow’s node'],
        },
        comprehensiveCorrect: {
          comprehensiveText:
            'Injection into right arm is preferred to avoid image interpretation error. Beware of the possibility of image interpretation as stasis in left axillary vein may be misinterpreted as metastatic lymph nodes (Virchow’s node).',
        },
      },
    ],
    '6': [
      {
        number: 5,
        question: {
          questionText:
            'If Axumin scanning is started after the optimal time window, what impact could it have on the normal biodistribution?',
        },
        options: [
          'Increased blood pool',
          'Increased muscle uptake',
          'Increased bladder activity',
          'Decreased muscle activity',
          'A and B',
          'B and C',
        ],
        answerIds: [15, 16, 17, 18, 19, 20],
        correct: {
          option: 'F',
          correctText:
            'If scanning is started late, biodistribution may be altered (eg, increased muscle uptake, increased bladder activity), and this should be taken into account during image interpretation.',
        },
        comprehensiveQuestion:
          'If Axumin scanning is started too late, what impact could it have on the normal biodistribution?',
      },
      {
        number: 6,
        question: {
          questionText:
            'Although urinary excretion is generally low, why is it important to have the patient void 30 minutes to 60 minutes prior to the scan?',
        },
        options: [
          'To ensure patients are comfortable during the scan',
          'To decrease the bladder size and allow better visualization of the adjacent structures',
          'Due to the possibility of early appearance of bladder activity',
          'To increase urinary bladder radioactivity',
        ],
        answerIds: [21, 22, 23, 24],
        correct: {
          option: 'C',
          correctText:
            'If patient voids within 30 minutes prior to start of scan, there is the possibility of early appearance of bladder activity and this should be taken into account during image interpretation. This activity is usually generalized, but occasionally may be focal in nature (simulating appearance of a nodule adjacent to the bladder wall), at least on the early images.',
        },
      },
    ],
    '9': [
      {
        number: 7,
        question: {
          questionText: 'What should be the predominant method of Axumin interpretation?',
        },
        options: [
          'Quantitative, based on detection of malignancy in prostate bed',
          'Quantitative, based on detection of bone metastases',
          'Qualitative, based on typical sites of recurrence of prostate cancer',
          'Qualitative, based on detection of lymph node metastases',
        ],
        answerIds: [25, 26, 27, 28],
        correct: {
          option: 'C',
          correctText:
            'Imaging interpretation is predominantly qualitative, based on typical sites of recurrence of prostate cancer.',
        },
      },
      {
        number: 8,
        question: {
          questionText:
            'What are the critically important details readers should know when interpreting Axumin PET/CT scans?',
        },
        options: [
          'Typical sites of metastases and recurrence of prostate cancers',
          'Normal distribution of Axumin',
          'Normal variants and potential pitfalls',
          'A and B',
          'All of the above',
        ],
        answerIds: [29, 30, 31, 32, 33],
        correct: {
          option: 'E',
          correctText:
            'It is critically important for the reader to know typical sites of metastases and recurrence of prostate cancers, as well as the normal distribution of Axumin, normal variants, and potential pitfalls.',
        },
        comprehensiveQuestion: 'What are the critically important things readers should know when interpreting Axumin?',
      },
      {
        number: 9,
        question: {
          questionText: 'The confidence level for Axumin can be improved by utilizing what information?',
        },
        options: [
          'Patient medical history',
          'Laboratory results',
          'Bone scans',
          'CT and MRI scans',
          'All of the above',
        ],
        answerIds: [34, 35, 36, 37, 38],
        correct: {
          option: 'E',
          correctText:
            'As with any imaging, full correlation with all available information (eg, medical history, laboratory results, bone scans, CT and MRI scans) should occur and may improve the confidence level of interpretation of the PET scan.',
        },
      },
    ],
    '10': [
      {
        number: 10,
        question: {
          questionText: 'What dimension of the lymph node should be used for interpretation?',
        },
        options: ['Short axis dimension < or ≥ 1 cm', 'Maximum dimension < or ≥ 1 cm', 'A and B'],
        answerIds: [39, 40, 41],
        correct: {
          option: 'B',
          correctText:
            'Thresholds based on lymph node sizes (ie, < or ≥ 1 cm), referred to throughout the interpretation criteria, are based on the maximum dimension of the lymph node for the purpose of visual interpretation.',
        },
      },
      {
        number: 11,
        question: {
          questionText: 'What projection is most useful for visualizing a bone lesion?',
        },
        options: [
          'Volume rendering (VR)',
          'Multiplanar reformation (MRP)',
          'Minimum intensity projection (MinIP)',
          'Maximum intensity projection (MIP)',
        ],
        answerIds: [42, 43, 44, 45],
        correct: {
          option: 'D',
          correctText: 'MIP may be useful for the detection of lesions in bone, as well as in lymph nodes.',
        },
        comprehensiveCorrect: {
          comprehensiveText:
            'Maximum Intensity Projection (MIP) may be useful for the detection of lesions in bone, as well as in lymph nodes.',
        },
      },
    ],
    '13': [
      {
        number: 12,
        question: {
          questionText: 'In the example seen below, there is recurrence in the prostate gland.',
          questionImages: [IMAGE_PATHS.BIOPSY_MALIGNANT],
        },
        options: ['True', 'False'],
        answerIds: [46, 47],
        optionsUnlabeled: true,
        correct: {
          option: 'True',
          correctText: 'There is recurrence in the prostate gland.',
        },
        comprehensiveQuestion: 'In the example seen, there is recurrence in the prostate gland.',
        comprehensiveCorrect: {
          comprehensiveText: 'True',
        },
      },
      {
        number: 13,
        question: {
          questionText:
            'For the prostate bed post-prostatectomy, what is the most useful image plane to evaluate the anastomotic site?',
        },
        options: ['Coronal images', 'Axial images', 'Sagittal images', 'All of the above'],
        answerIds: [48, 49, 50, 51],
        correct: {
          option: 'C',
          correctText: 'Sagittal images are useful in the evaluation of the anastomotic site.',
        },
      },
      {
        number: 14,
        question: {
          questionText: 'In what situations is an MRI correlation suggested in an intact prostate?',
        },
        options: [
          'When there is uptake on anatomical correlate < 1 cm, significantly greater than blood pool (ie, close to bone marrow), that may also be considered suspicious for malignancy',
          'When there is focal uptake with calcification that may indicate benign inflammation',
          'A and B',
        ],
        answerIds: [52, 53, 54],
        correct: {
          option: 'C',
          correctText:
            'Uptake on anatomical correlate < 1 cm, significantly greater than blood pool (ie, close to bone marrow), may also be considered suspicious for malignancy; MRI correlation is suggested. Focal uptake with calcification may indicate benign inflammation; MRI correlation is suggested.',
        },
      },
    ],
    '14': [
      {
        number: 15,
        question: {
          questionText: 'When does uptake in seminal vesicles cause increased suspicion?',
        },
        options: [
          'Symmetric bilateral uptake similar to blood pool',
          'Asymmetric seminal vesicle uptake between blood pool and marrow',
          'Diffuse asymmetrical uptake below blood pool',
          'All of the above',
        ],
        answerIds: [55, 56, 57, 58],
        correct: {
          option: 'B',
          correctText:
            'Asymmetric seminal vesicle uptake between blood pool and marrow may increase the suspicion for malignancy; consider pelvis MRI for further characterization.',
        },
      },
    ],
    '15': [
      {
        number: 16,
        question: {
          questionText:
            'Lymph nodes of what size in typical sites of recurrence of prostate cancer should have a <b>HIGHER</b> threshold of Axumin positivity?',
        },
        options: ['≥ 1 cm', '< 1 cm'],
        answerIds: [59, 60],
        correct: {
          option: 'A',
          correctText:
            'Uptake in lymph nodes ≥ 1 cm in a typical site of recurrence of prostate cancer should have a higher threshold for positivity relative to lymph nodes < 1 cm.',
        },
      },
      {
        number: 17,
        question: {
          questionText:
            'Lymph nodes of what size in typical sites of recurrence of prostate cancer should have a <b>LOWER</b> threshold of Axumin positivity?',
        },
        options: ['≥ 1 cm', '< 1 cm'],
        answerIds: [61, 62],
        correct: {
          option: 'B',
          correctText:
            'Uptake in a lymph node < 1 cm in a typical site of recurrence of prostate cancer should have a lower threshold for positivity, which is considered significantly greater than blood pool (ie, close to, equal to, or greater than marrow).',
        },
      },
      {
        number: 18,
        question: {
          questionText: 'What pattern of uptake in lymph nodes would be considered suspicious?',
        },
        options: [
          'A group of nodes in a typical location is more suspicious than a solitary node',
          'A single node with higher uptake than surrounding lymph nodes may still be suspicious even if not meeting the threshold for malignancy',
          'A and B',
        ],
        answerIds: [63, 64, 65],
        correct: {
          option: 'C',
          correctText:
            'Depending on category, a group of nodes in a typical location is more suspicious than a solitary node, and in that case a single node with higher uptake than surrounding lymph nodes may still be suspicious even if not meeting the threshold for malignancy.',
        },
      },
      {
        number: 19,
        question: {
          questionText:
            'Inguinal, hilar, and axillary nodes should be considered suspicious if they have mild symmetrical update.',
        },
        options: ['True', 'False'],
        answerIds: [66, 67],
        optionsUnlabeled: true,
        correct: {
          option: 'False',
          correctText:
            'For atypical sites of recurrence (eg, inguinal, hilar, and axillary nodes) mild symmetric uptake is considered physiologic uptake.',
        },
        comprehensiveQuestion:
          'Should inguinal, hilar, and axillary nodes be considered as suspicious if they have mild symmetrical uptake?',
        comprehensiveCorrect: {
          comprehensiveText:
            'False, for atypical sites of recurrence (e.g., inguinal, hilar, and axillary nodes) mild symmetric uptake is considered physiologic uptake.',
        },
      },
    ],
    '16': [
      {
        number: 20,
        question: {
          questionText:
            'Should you consider an alternative imaging option, after observing sclerosis on the CT without Axumin uptake on PET?',
        },
        options: ['Yes', 'No'],
        answerIds: [68, 69],
        correct: {
          option: 'A',
          correctText:
            'Yes, a bone abnormality visualized on CT (eg, sclerosis without uptake) may still represent a metastasis. Alternative imaging, for example MRI, <sup>18</sup>F-NaF PET, <sup>99m</sup>Tc SPECT/CT bone scan or standard bone scintigraphy, should be considered.',
        },
      },
    ],
    '17': [
      {
        number: 21,
        question: {
          questionText: 'When should you consider further evaluation of the bladder after Axumin injection?',
        },
        options: [
          'Diffuse symmetric bladder wall thickening',
          'Mild symmetric bladder activity',
          'Asymmetric significant bladder activity',
          'Focal bladder wall thickening',
        ],
        answerIds: [70, 71, 72, 73],
        correct: {
          option: 'C',
          correctText: 'Asymmetric significant uptake may represent malignancy and should be further evaluated.',
        },
      },
    ],
    '18': [
      {
        number: 22,
        question: {
          questionText: 'Does PSA level at the time of Axumin PET/CT have an impact on likelihood of detection?',
        },
        options: ['Yes', 'No'],
        answerIds: [74, 75],
        correct: {
          option: 'A',
          correctText:
            'Yes, PSA levels seem to have an impact on the detection rate of Axumin. In general, patients with negative scans had lower PSA levels than those with positive scans.',
        },
      },
      {
        number: 23,
        optionsUnlabeled: true,
        question: {
          questionText:
            'Physicians should educate themselves on potential areas for incidental findings with Axumin PET/CT.',
        },
        options: ['True', 'False'],
        answerIds: [76, 77],
        correct: {
          option: 'True',
          correctText:
            'Physicians should be aware of the potential for Axumin uptake in areas of benign pathology and other incidental cancers. These potential incidental findings with Axumin are outside of its indicated use for PET imaging in men with suspected prostate cancer recurrence.',
        },
        comprehensiveCorrect: {
          comprehensiveText:
            'True, Physicians should be aware of the potential for Axumin uptake in areas of benign pathology and other incidental cancers. These potential incidental findings with Axumin are outside of its indicated use for positron emission tomography (PET) imaging in men with suspected prostate cancer recurrence.',
        },
      },
    ],
  },
  axuminAcquisition: {
    '3': [
      {
        number: 1,
        question: {
          questionText:
            'Amino acid transporters are upregulated into many cancer cells. What are the principal transporters involved in Axumin uptake for more aggressive phenotypes of prostate cancer?',
        },
        options: ['LAT2 and ASCT1', 'SLC7A8 and ASCT1', 'LAT1 and ASCT2', 'LAT3 and ASCT1'],
        answerIds: [78, 79, 80, 81],
        correct: {
          option: 'C',
          correctText:
            'LAT1 and ASCT2 expression levels have been correlated with a more aggressive phenotype of prostate cancer.',
        },
      },
      {
        number: 2,
        question: {
          questionText: 'Does uptake in muscle increase or decrease over time post Axumin injection?',
        },
        options: ['Increase', 'Decrease'],
        answerIds: [82, 83],
        correct: {
          option: 'A',
          correctText: 'Over time, Axumin distribution to the skeletal muscle <strong>increases</strong>.',
        },
        comprehensiveCorrect: {
          comprehensiveText: 'Over time, Axumin distribution to the skeletal muscle increases.',
        },
      },
      {
        number: 3,
        question: {
          questionText:
            'To take advantage of the highest tumor-to-normal tissue contrast, what is the critical time frame within which to begin PET imaging in the pelvis?',
        },
        options: [
          '10 to 20 minutes after injection',
          '1 to 3 minutes after injection',
          '3 to 5 minutes after injection',
          'Up to 90 minutes after injection',
        ],
        answerIds: [84, 85, 86, 87],
        correct: {
          option: 'C',
          correctText:
            'PET scanning should begin 3 to 5 minutes after injection. The tumor-to-normal tissue contrast is highest between 4 and 10 minutes post injection.',
        },
      },
    ],
    '7': [
      {
        number: 4,
        question: {
          questionText: 'What is the RECOMMENDED dose in mCi for all adult patients receiving an Axumin injection?',
        },
        options: ['5mCi (185 MBq)', '10 mCi (370 MBq)', '13 mci (481 MBq)', '20 mCi (740 MBq)'],
        answerIds: [88, 89, 90, 91],
        correct: {
          option: 'B',
          correctText: 'Recommended dose is 10 mCi (370 MBq) for adult patients receiving an Axumin injection.',
        },
        comprehensiveCorrect: {
          comprehensiveText: 'Recommended dose is 10 mCi (370 MBq) for adult patients receiving an Axumin injection.',
        },
      },
      {
        number: 5,
        question: {
          questionText:
            'Axumin injection into the _______ arm is preferred to avoid image interpretation error due to ______?',
        },
        options: [
          'Right AND stasis in left axillary vein',
          'Right AND urinary stasis',
          'Left AND misregistration',
          'Left AND hypermetabolic lesions',
        ],
        answerIds: [92, 93, 94, 95],
        correct: {
          option: 'A',
          correctText:
            'Injection into right arm is preferred to avoid image interpretation error. Beware of the possibility of image interpretation as stasis in left axillary vein may be misinterpreted as metastatic lymph nodes (Virchow’s node).',
          images: [IMAGE_PATHS.STATIS_VEIN, IMAGE_PATHS.VIRCHOWS_NODE],
          captions: ['Stasis (or venous trapping in axillary vein (SUV<sub>max</sub> = 201)', 'Virchow’s node'],
        },
      },
    ],
    '8': [
      {
        number: 6,
        question: {
          questionText:
            'If Axumin scanning is started after the optimal time window, what impact could it have on the normal biodistribution?',
        },
        options: [
          'Increased blood pool',
          'Increased muscle uptake',
          'Increased bladder activity',
          'Decreased muscle activity',
          'A and B',
          'B and C',
        ],
        answerIds: [96, 97, 98, 99, 100, 101],
        correct: {
          option: 'F',
          correctText:
            'If scanning is started late, biodistribution may be altered (eg, increased muscle uptake, increased bladder activity), and this should be taken into account during image interpretation.',
        },
      },
      {
        number: 7,
        question: {
          questionText:
            'Drag the appropriate responses to the corresponding column.<br/>What is recommended and what is not recommended?',
        },
        answerIds: [102],
        options: [],
        childNode: Acquisition07,
        correct: {
          images: [IMAGE_PATHS.DRAG_DROP_ANSWERS],
          correctText: '',
          customText: '<b>Correct answers are:</b>',
        },
        comprehensiveQuestion: 'What is recommended and what is not recommended?',
      },
      {
        number: 8,
        question: {
          questionText:
            'Although urinary excretion is generally low, why is it important to have the patient void 30 minutes to 60 minutes prior to the scan?',
        },
        options: [
          'To ensure patients are comfortable during the scan',
          'To decrease the bladder size and allow better visualization of the adjacent structures',
          'Due to the possibility of early appearance of bladder activity',
          'To increase urinary bladder radioactivity',
        ],
        answerIds: [103, 104, 105, 106],
        correct: {
          option: 'C',
          correctText:
            'If patient voids within 30 minutes prior to start of scan, there is the possibility of early appearance of bladder activity and this should be taken into account during image interpretation. This activity is usually generalized, but occasionally may be focal in nature (simulating appearance of a nodule adjacent to the bladder wall), at least on the early images.',
        },
        comprehensiveCorrect: {
          comprehensiveText:
            'If patient voids within 30 minutes prior to start of scan, there is the possibility of early appearance of bladder activity and this should be taken into account during image interpretation. This activity is usually generalized, but occasionally may be focal in nature (simulating appearance of a nodule adjacent to the bladder wall), at least on the early images.',
        },
      },
    ],
    '9': [
      {
        number: 9,
        question: {
          questionText: 'For Axumin injection and flush, how should the arm be positioned?',
        },
        options: ['Arms above head', 'Arms adjacent to the torso'],
        answerIds: [107, 108],
        correct: {
          option: 'B',
          correctText:
            'Administer dose as an intravenous bolus injection while patient is positioned in PET/CT scanner, with their arms adjacent to the torso.',
        },
      },
      {
        number: 10,
        question: {
          questionText: 'At the start of Axumin PET imaging, where should the first bed position be centered?',
        },
        options: [
          'The base of the skull',
          'The patient’s left side at the mid-thigh, just below the pelvis',
          'The patient’s right side, just above the pelvis',
          'The prostate bed (approximated by the pubic symphysis)',
        ],
        answerIds: [109, 110, 111, 112],
        correct: {
          option: 'D',
          correctText:
            'It is very important that the prostate bed is centrally positioned within the first bed position and that inguinal lymph nodes are included (as shown in the image below).',
          images: [IMAGE_PATHS.FIRST_BED_POSITION],
        },
        comprehensiveCorrect: {
          comprehensiveText:
            'It is very important that the prostate bed is centrally positioned within the first bed position and that inguinal lymph nodes are included.',
        },
      },
    ],
  },
  poslumaAcquisition: {
    '3': [
      {
        number: 1,
        question: {
          questionText: 'POSLUMA is a fluorine radiolabeled radiohybrid <i>diagnostic</i> agent targeting',
        },
        // Open question about initials for A and C options
        options: ['PSA', 'Human kallikrein 2', 'PSMA', 'All prostate cancer markers'],
        answerIds: [113, 114, 115, 116],
        correct: {
          option: 'C',
          correctText: 'Prostate-specific membrane antigen.',
        },
      },
      // Commenting this out just in case they want it back in
      // {
      //   number: 2,
      //   question: {
      //     questionText: 'Compared to gallium, fluorine radiolabeling of POSLUMA radiohybrid results in ________.',
      //   },
      //   options: [
      //     'A half-life of 110 minutes',
      //     'Improved contrast resolution, due to low positron energy',
      //     'Reduced image noise, due to low positron energy',
      //     'All of the above',
      //   ],
      //   answerIds: [117, 118, 119, 120],
      //   correct: {
      //     option: 'D',
      //     correctText: 'All of the above.',
      //   },
      // },
    ],
    '6': [
      {
        number: 2,
        question: {
          questionText: 'Which of the following statements are true regarding patient preparation for a POSLUMA scan?',
        },
        options: [
          'Patient must be NPO for 4 hours prior to administration',
          'Ensure adequate hydration prior to as well as after administration',
          'Encourage the patient to void frequently for the first few hours post injection',
          'B and C',
        ],
        answerIds: [121, 122, 123, 124],
        correct: {
          option: 'D',
          correctText: 'B and C',
        },
      },
    ],
    '7': [
      {
        number: 3,
        question: {
          questionText: 'What is the preferred method to administer the 8 mCi dose of POSLUMA?',
        },
        options: [
          'Slow infusion (over 30 seconds) into the left antecubital vein followed by a 0.9% sterile saline drip',
          'Intravenous bolus injection followed by an intravenous flush of 0.9% sterile sodium chloride',
          'Intravenous bolus into the right radial vein followed by 40 mg of furosemide',
          'Intravenous bolus into the left ulnar vein followed by 50 mL 5% dextrose drip',
        ],
        answerIds: [125, 126, 127, 128],
        correct: {
          option: 'B',
          correctText: 'Intravenous bolus injection followed by an intravenous flush of 0.9% sterile sodium chloride.',
        },
      },
    ],
    '8': [
      {
        number: 4,
        question: {
          questionText:
            'For POSLUMA image acquisition, begin image acquisition approximately 30 minutes after POSLUMA injection.',
        },
        options: ['True', 'False'],
        answerIds: [129, 130],
        correct: {
          option: 'False',
          correctText:
            'Begin image acquisition approximately 60 minutes (range 50-70 minutes) after POSLUMA injection; beginning imaging between 70-90 minutes after injection should be at the discretion of the supervising physician only.',
        },
      },
    ],
  },
  poslumaInterpretation: {
    '3': [
      {
        number: 1,
        question: {
          questionText: 'POSLUMA is a fluorine radiolabeled radiohybrid <i>diagnostic</i> agent targeting',
        },
        // Open question about initials for A and C options
        options: ['PSA', 'Human kallikrein 2', 'PSMA', 'All prostate cancer markers'],
        answerIds: [131, 132, 133, 134],
        correct: {
          option: 'C',
          correctText: 'Prostate-specific membrane antigen.',
        },
      },
      // {
      //   number: 2,
      //   question: {
      //     questionText: 'Fluorine radiolabeling of POSLUMA radiohybrid results in ________.',
      //   },
      //   options: [
      //     'A half-life of 110 minutes',
      //     'Improved contrast resolution, due to low positron energy',
      //     'Reduced image noise, due to low positron energy',
      //     'All of the above',
      //   ],
      //   answerIds: [135, 136, 137, 138],
      //   correct: {
      //     option: 'D',
      //     correctText: 'All of the above.',
      //   },
      // },
    ],
    '6': [
      {
        number: 2,
        question: {
          questionText: 'Which of the following statements are true regarding patient preparation for a POSLUMA scan?',
        },
        options: [
          'Patient must be NPO for 4 hours prior to administration',
          'Ensure adequate hydration prior to as well as after administration',
          'Encourage the patient to void frequently for the first few hours post injection',
          'B and C',
        ],
        answerIds: [139, 140, 141, 142],
        correct: {
          option: 'D',
          correctText: 'B and C',
        },
      },
    ],
    '7': [
      {
        number: 3,
        question: {
          questionText: 'What is the preferred method to administer the 8 mCi dose of POSLUMA?',
        },
        options: [
          'Slow infusion (over 30 seconds) into the left antecubital vein followed by a 0.9% sterile saline drip',
          'Intravenous bolus injection followed by an intravenous flush of 0.9% sterile sodium chloride',
          'Intravenous bolus into the right radial vein followed by 40 mg of furosemide',
          'Intravenous bolus into the left ulnar vein followed by 50 mL 5% dextrose drip',
        ],
        answerIds: [143, 144, 145, 146],
        correct: {
          option: 'B',
          correctText: 'Intravenous bolus injection followed by an intravenous flush of 0.9% sterile sodium chloride.',
        },
      },
    ],
    '8': [
      {
        number: 4,
        question: {
          questionText:
            'For POSLUMA image acquisition, begin image acquisition approximately 30 minutes after POSLUMA injection.',
        },
        options: ['True', 'False'],
        answerIds: [147, 148],
        correct: {
          option: 'False',
          correctText:
            'Begin image acquisition approximately 60 minutes (range 50-70 minutes) after POSLUMA injection; beginning imaging between 50-70 minutes after injection should be at the discretion of the supervising physician only.',
        },
      },
    ],
    '12': [
      {
        number: 5,
        question: {
          questionText:
            'During image interpretation with POSLUMA, which of the following might be considered suspicious lesions?',
        },
        options: [
          'Tissue exhibiting uptake greater than physiologic uptake in that tissue',
          'Tissue exhibiting uptake greater than adjacent background if no physiologic uptake is expected',
          'Tissue exhibiting uptake less than physiologic uptake in that tissue',
          'Tissue exhibiting uptake less than adjacent background if no physiologic uptake is expected',
          'A and B',
        ],
        answerIds: [149, 150, 151, 152, 153],
        correct: {
          option: 'E',
          correctText: 'A and B',
        },
      },
      {
        number: 6,
        question: {
          questionText:
            'It is important to conduct POSLUMA image interpretation in reference to anatomical images, such as CT.',
        },
        options: ['True', 'False'],
        answerIds: [154, 155],
        correct: {
          option: 'True',
          correctText:
            'Coregistered anatomical images will lead to a more complete interpretation, particularly for bone lesions.',
        },
      },
    ],
    '13': [
      {
        number: 7,
        question: {
          questionText:
            'SUV scale adjustment may be appropriate in some instances. Larger SUV scales (e.g., 0-20) could aid in the complete interpretation of which of the following?',
        },
        options: [
          'POSLUMA uptake in the rectum',
          'POSLUMA uptake in the inguinal lymph nodes',
          'POSLUMA uptake in the sacral ganglion',
          'POSLUMA uptake in the liver',
          'A, B, D',
          'All of the above',
        ],
        answerIds: [156, 157, 158, 159, 160, 161],
        correct: {
          option: 'F',
          correctText: 'All of the above',
        },
      },
    ],
  },
};

export default LEARNING_CHECK;
